import { useEffect, useMemo, useState } from 'react';
import { Address } from '@ton/core';
import { TonConnectButton, useTonConnectUI, useTonWallet } from '@tonconnect/ui-react';
import {
    claimArtifactPayload,
    CLAIM_VALUE,
    CONTROL_VALUE,
    DEPLOY_VALUE,
    MAINNET_CHAIN,
    prepareCollection,
    PreparedCollection,
    sameAddress,
    setIssuancePayload,
    setK17RecipientPayload,
    shortAddress,
    shortHash,
    TESTNET_CHAIN,
} from './lib/ton';

type ActionKind =
    | 'deploy'
    | 'enable'
    | 'pause'
    | 'authorize-k17'
    | 'claim-k17'
    | 'claim-k18';

type Confirmation = {
    kind: ActionKind;
    title: string;
    recipient?: string;
};

const releaseEnabled = import.meta.env.VITE_RELEASE_ENABLED === 'true';
const configuredAdmin = import.meta.env.VITE_ADMIN_ADDRESS?.trim();
const configuredCollection = import.meta.env.VITE_COLLECTION_ADDRESS?.trim();
const configuredBase = import.meta.env.VITE_PUBLIC_BASE_URL?.trim();

function isPermanentExternalBase(base: string): boolean {
    try {
        const url = new URL(base);
        return (
            url.protocol === 'https:' &&
            !['localhost', '127.0.0.1'].includes(url.hostname) &&
            !url.hostname.endsWith('.chatgpt.site') &&
            !url.hostname.endsWith('.invalid')
        );
    } catch {
        return false;
    }
}

function actionTon(kind: ActionKind): string {
    return ['deploy', 'claim-k17', 'claim-k18'].includes(kind) ? '0.10' : '0.05';
}

function App() {
    const wallet = useTonWallet();
    const [tonConnect] = useTonConnectUI();
    const [prepared, setPrepared] = useState<PreparedCollection | null>(null);
    const [prepareError, setPrepareError] = useState('');
    const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
    const [pending, setPending] = useState(false);
    const [notice, setNotice] = useState('');
    const [k17Address, setK17Address] = useState('');
    const [deployedThisSession, setDeployedThisSession] = useState(false);

    const publicBase = (configuredBase || window.location.origin).replace(/\/$/, '');
    const walletAddress = wallet?.account.address;
    const network = wallet?.account.chain;
    const isTestnet = network === TESTNET_CHAIN;
    const isMainnet = network === MAINNET_CHAIN;
    const isAdmin = sameAddress(walletAddress, configuredAdmin);
    const externalBaseReady = isPermanentExternalBase(publicBase);

    useEffect(() => {
        let active = true;
        if (!configuredAdmin || !externalBaseReady) {
            setPrepared(null);
            return;
        }
        prepareCollection(configuredAdmin, publicBase)
            .then((value) => {
                if (active) {
                    setPrepared(value);
                    setPrepareError('');
                }
            })
            .catch((error: unknown) => {
                if (active) setPrepareError(error instanceof Error ? error.message : 'Ошибка подготовки');
            });
        return () => {
            active = false;
        };
    }, [publicBase, externalBaseReady]);

    const calculatedAddress = prepared?.address.toString({ testOnly: true, bounceable: true });
    const collectionMatches = !configuredCollection || sameAddress(calculatedAddress, configuredCollection);
    const configuredAsDeployed = Boolean(configuredCollection && collectionMatches);
    const canUseCollection = configuredAsDeployed || deployedThisSession;

    const blockingReasons = useMemo(() => {
        const reasons: string[] = [];
        if (!releaseEnabled) reasons.push('режим выпуска выключен в конфигурации');
        if (!configuredAdmin) reasons.push('не закреплён адрес администратора');
        if (!externalBaseReady) reasons.push('нет постоянного независимого HTTPS-домена');
        if (configuredCollection && !collectionMatches) reasons.push('адрес коллекции не совпадает с кодом и протоколом');
        if (wallet && !isTestnet) reasons.push('кошелёк подключён не к TON Testnet');
        if (prepareError) reasons.push(prepareError);
        return reasons;
    }, [wallet, isTestnet, collectionMatches, prepareError, externalBaseReady]);

    const baseReady = blockingReasons.length === 0 && Boolean(wallet && prepared);
    const adminReady = baseReady && isAdmin;

    function openAction(kind: ActionKind) {
        setNotice('');
        const adminAction = ['deploy', 'enable', 'pause', 'authorize-k17', 'claim-k18'].includes(kind);
        if (!baseReady || (adminAction && !isAdmin)) {
            setNotice(adminAction ? 'Для этой операции подключите Testnet-кошелёк администратора K18.' : 'Подключите TON Testnet-кошелёк.');
            return;
        }
        if (kind !== 'deploy' && !canUseCollection) {
            setNotice('Сначала разверните и закрепите коллекцию в TON Testnet.');
            return;
        }
        if (kind === 'authorize-k17') {
            try {
                Address.parse(k17Address);
            } catch {
                setNotice('Введите полный корректный адрес TON Testnet для K17.');
                return;
            }
            setConfirmation({ kind, title: 'Закрепить адрес K17', recipient: k17Address });
            return;
        }

        const titles: Record<Exclude<ActionKind, 'authorize-k17'>, string> = {
            deploy: 'Развернуть коллекцию',
            enable: 'Открыть окно выпуска',
            pause: 'Остановить выпуск',
            'claim-k17': 'Принять K17',
            'claim-k18': 'Принять K18',
        };
        setConfirmation({
            kind,
            title: titles[kind],
            recipient: kind.startsWith('claim-') ? walletAddress : undefined,
        });
    }

    async function confirmAction() {
        if (!confirmation || !walletAddress || !prepared || !baseReady) return;
        setPending(true);
        setNotice('');
        try {
            if (confirmation.kind === 'deploy') {
                await tonConnect.sendTransaction({
                    validUntil: Math.floor(Date.now() / 1000) + 600,
                    network: TESTNET_CHAIN,
                    messages: [{
                        address: calculatedAddress!,
                        amount: DEPLOY_VALUE.toString(),
                        stateInit: prepared.stateInitBase64,
                    }],
                });
                setDeployedThisSession(true);
                setNotice('Развёртывание подтверждено кошельком. Дождитесь появления коллекции в Testnet Explorer.');
            } else {
                let payload: string;
                let amount = CONTROL_VALUE;
                if (confirmation.kind === 'enable') payload = setIssuancePayload(true);
                else if (confirmation.kind === 'pause') payload = setIssuancePayload(false);
                else if (confirmation.kind === 'authorize-k17') payload = setK17RecipientPayload(confirmation.recipient!);
                else {
                    payload = claimArtifactPayload(confirmation.kind === 'claim-k17' ? 'k17' : 'k18');
                    amount = CLAIM_VALUE;
                }

                await tonConnect.sendTransaction({
                    validUntil: Math.floor(Date.now() / 1000) + 600,
                    network: TESTNET_CHAIN,
                    messages: [{
                        address: calculatedAddress!,
                        amount: amount.toString(),
                        payload,
                    }],
                });

                const notices: Record<Exclude<ActionKind, 'deploy'>, string> = {
                    enable: 'Окно выпуска открыто. Контракт теперь принимает однократные запросы K17 и K18.',
                    pause: 'Выпуск остановлен. Уже созданные артефакты не меняются.',
                    'authorize-k17': 'Адрес K17 закреплён. Теперь именно этот кошелёк должен сам подтвердить принятие K17.',
                    'claim-k17': 'Запрос K17 подписан её кошельком. Проверяйте выпуск в Testnet Explorer.',
                    'claim-k18': 'Запрос K18 подписан администратором. Проверяйте выпуск в Testnet Explorer.',
                };
                setNotice(notices[confirmation.kind]);
            }
            setConfirmation(null);
        } catch (error) {
            setNotice(error instanceof Error ? error.message : 'Операция отменена или не выполнена.');
        } finally {
            setPending(false);
        }
    }

    const explorerUrl = calculatedAddress ? `https://testnet.tonviewer.com/${calculatedAddress}` : undefined;

    return (
        <main>
            <header className="site-header">
                <a className="wordmark" href="#origin" aria-label="KÆMILITH: ORIGIN">
                    <span>KÆMILITH</span>
                    <small>ORIGIN / K17 · K18</small>
                </a>
                <TonConnectButton />
            </header>

            <section className="hero" id="origin">
                <div className="eyebrow"><span /> INDEPENDENT TON TESTNET</div>
                <h1>Два самостоятельных<br />начала в сети.</h1>
                <p className="lead">
                    K17 и K18 — два самостоятельных артефакта одного ORIGIN. Их связывает взаимный выбор,
                    но ни один из них не является недостающей половиной другого.
                </p>
                <div className="network-line">
                    <span className={`status-dot ${isTestnet ? 'ok' : isMainnet ? 'danger' : ''}`} />
                    {!wallet && 'Кошелёк не подключён'}
                    {wallet && isTestnet && `TON Testnet · ${shortAddress(walletAddress)}`}
                    {wallet && isMainnet && 'Подключён Mainnet — операции заблокированы'}
                    {wallet && !isTestnet && !isMainnet && 'Неизвестная сеть — операции заблокированы'}
                </div>
            </section>

            <section className="protocol" aria-label="Протокол выпуска">
                <div className="protocol-copy">
                    <span className="chapter">01 / RELEASE CONTROL</span>
                    <h2>Сначала закрепляется<br />источник.</h2>
                    <p>
                        Pair root и публичные отпечатки K17/K18 входят в адрес коллекции и не имеют функции изменения.
                        Контракт стартует на паузе и допускает только однократное принятие K17 и K18.
                    </p>
                </div>
                <div className="control-panel">
                    <div className="control-row"><span>Сеть</span><b>TON Testnet</b></div>
                    <div className="control-row"><span>Администратор</span><b>{shortAddress(configuredAdmin)}</b></div>
                    <div className="control-row"><span>Коллекция</span><b>{shortAddress(calculatedAddress)}</b></div>
                    <div className="control-row"><span>Pair root</span><b>{shortHash(prepared?.protocol.pair_root_sha256)}</b></div>
                    <div className="control-row"><span>Транзакции</span><b>{releaseEnabled ? 'разрешены конфигурацией' : 'выключены'}</b></div>
                    {blockingReasons.length > 0 && (
                        <div className="blockers" role="status">
                            <strong>До выпуска осталось:</strong>
                            <ul>{blockingReasons.map((reason) => <li key={reason}>{reason}</li>)}</ul>
                        </div>
                    )}
                    <button className="primary" disabled={!adminReady || canUseCollection} onClick={() => openAction('deploy')}>
                        {canUseCollection ? 'Коллекция закреплена' : 'Развернуть коллекцию'} <span>→</span>
                    </button>
                    <div className="action-stack compact">
                        <button className="secondary" disabled={!adminReady || !canUseCollection} onClick={() => openAction('enable')}>Открыть выпуск <span>→</span></button>
                        <button className="secondary outline" disabled={!adminReady || !canUseCollection} onClick={() => openAction('pause')}>Поставить на паузу <span>‖</span></button>
                    </div>
                    {explorerUrl && <a className="text-link" href={explorerUrl} target="_blank" rel="noreferrer">Открыть расчётный адрес в Explorer ↗</a>}
                </div>
            </section>

            <section className="artifacts" aria-label="Артефакты K17 и K18">
                <article className="artifact-card rose">
                    <div className="artifact-image"><img src="/artifacts/k17.png" alt="Абстрактный артефакт K17" /></div>
                    <div className="artifact-body">
                        <span className="chapter">K17 / PUBLIC IDENTITY ONLY</span>
                        <h2>K17.</h2>
                        <p>Публично существует только K17: без настоящего имени, лица, голоса и узнаваемых личных деталей. Администратор закрепляет лишь её Testnet-адрес.</p>
                        <label className="field">
                            <span>Testnet-адрес K17</span>
                            <input value={k17Address} onChange={(event) => setK17Address(event.target.value)} placeholder="0:… или kQ…" autoComplete="off" />
                        </label>
                        <p className="microcopy">После закрепления адрес виден в TON. Принятие подтверждается отдельной транзакцией самого кошелька K17.</p>
                        <div className="action-stack">
                            <button className="secondary" disabled={!adminReady || !canUseCollection} onClick={() => openAction('authorize-k17')}>Закрепить адрес K17 <span>→</span></button>
                            <button className="secondary outline dark" disabled={!baseReady || !canUseCollection} onClick={() => openAction('claim-k17')}>Принять K17 этим кошельком <span>→</span></button>
                        </div>
                    </div>
                </article>

                <article className="artifact-card blue">
                    <div className="artifact-image"><img src="/artifacts/k18.png" alt="Абстрактный артефакт K18" /></div>
                    <div className="artifact-body">
                        <span className="chapter">K18 / ÆL’VORIN</span>
                        <h2>K18.</h2>
                        <p>K18 принимается администраторским Testnet-кошельком. Pair root связывает происхождение K17 и K18, не передавая право владения одним другому.</p>
                        <div className="recipient-box">
                            <span>Получатель</span>
                            <b>{shortAddress(walletAddress)}</b>
                        </div>
                        <button className="secondary" disabled={!adminReady || !canUseCollection} onClick={() => openAction('claim-k18')}>Принять K18 <span>→</span></button>
                    </div>
                </article>
            </section>

            {notice && <div className="notice" role="status">{notice}</div>}

            <section className="safety">
                <span className="chapter">02 / BOUNDARIES</span>
                <h2>Что этот контур не делает.</h2>
                <div className="safety-grid">
                    <p><b>Не раскрывает K17.</b><br />В публичных файлах нет её настоящего имени и узнаваемых личных данных.</p>
                    <p><b>Не продаёт.</b><br />Внутри нет цены, маркетплейса, обещания доходности или обмена на деньги.</p>
                    <p><b>Не хранит ключи.</b><br />Каждое действие подписывается только во внешнем TON-кошельке.</p>
                </div>
            </section>

            <footer>
                <span>KÆMILITH: ORIGIN · TESTNET BUILD 0.2</span>
                <nav><a href="/terms.html">Условия</a><a href="/privacy.html">Приватность</a></nav>
            </footer>

            {confirmation && (
                <div className="modal-backdrop" role="presentation" onMouseDown={() => !pending && setConfirmation(null)}>
                    <section className="modal" role="dialog" aria-modal="true" aria-labelledby="confirm-title" onMouseDown={(event) => event.stopPropagation()}>
                        <button className="close" onClick={() => setConfirmation(null)} disabled={pending} aria-label="Закрыть">×</button>
                        <span className="chapter">WALLET CONFIRMATION</span>
                        <h2 id="confirm-title">{confirmation.title}</h2>
                        <p>Проверьте каждое поле. Следующее подтверждение откроется уже в вашем кошельке.</p>
                        <dl>
                            <div><dt>Сеть</dt><dd>TON Testnet (-3)</dd></div>
                            <div><dt>Контракт</dt><dd>{shortAddress(calculatedAddress)}</dd></div>
                            {confirmation.recipient && <div><dt>Адрес</dt><dd>{shortAddress(confirmation.recipient)}</dd></div>}
                            <div><dt>Передаваемая сумма</dt><dd>{actionTon(confirmation.kind)} Testnet TON</dd></div>
                            <div><dt>Продажа</dt><dd>Нет</dd></div>
                        </dl>
                        <p className="fineprint">Testnet TON не имеет реальной стоимости. Фактическая комиссия показывается кошельком до подписи.</p>
                        <button className="primary" onClick={confirmAction} disabled={pending}>{pending ? 'Ожидаю кошелёк…' : 'Продолжить в кошельке'} <span>→</span></button>
                    </section>
                </div>
            )}
        </main>
    );
}

export default App;
